import type { Icon } from "@phosphor-icons/react";
import {
  ArrowUUpLeft,
  ArrowUUpRight,
  Code,
  CodeBlock,
  ListBullets,
  ListNumbers,
  Quotes,
  TextB,
  TextHOne,
  TextHThree,
  TextHTwo,
  TextItalic,
  TextStrikethrough,
} from "@phosphor-icons/react";
import type { Editor } from "@tiptap/react";
import { useEditorState } from "@tiptap/react";
import { useTranslation } from "react-i18next";
import { SelectionAiMenu } from "~/features/documents/editor/SelectionAiMenu";
import type { SelectionAction } from "~/features/documents/editor/selectionTypes";
import { cn } from "~/foundation/utils/cn";

function ToolbarButton({
  icon: IconComponent,
  label,
  active,
  disabled,
  onClick,
}: {
  icon: Icon;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active === true}
      disabled={disabled}
      // Mousedown would steal the editor's selection before the command runs.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded-sm text-text-secondary",
        "hover:bg-hover-bg hover:text-text disabled:opacity-40 disabled:hover:bg-transparent",
        active && "bg-hover-bg text-text",
      )}
    >
      <IconComponent size={16} weight={active ? "bold" : "regular"} aria-hidden />
    </button>
  );
}

function Separator() {
  return <span aria-hidden className="mx-1 h-4 w-px bg-divider" />;
}

/**
 * The AI segment of the toolbar (W6 §A2.3).
 *
 * Two things about it are load-bearing:
 *
 *  - **It appears for a read-only viewer who can think.** Upstream, the toolbar was hidden for
 *    a reader who could not comment; the delta widens that so somebody with the AI capability
 *    keeps the toolbar without edit rights. AI actions are available without write access —
 *    what they may DO is then decided by the server, which returns the propose lane rather
 *    than refusing outright.
 *  - **It only renders on a non-empty text selection.** There is nothing to act on otherwise,
 *    and the affordance is gated on the update ability by the host.
 */
export interface EditorToolbarAi {
  /** The merged, scoped catalogue. */
  actions: SelectionAction[];
  onSelectAction: (action: SelectionAction) => void;
  onDoAction: (action: SelectionAction) => void;
  onCustomAction?: () => void;
  lastLane?: "act" | "propose" | null;
  busy?: boolean;
}

export function EditorToolbar({ editor, ai }: { editor: Editor; ai?: EditorToolbarAi }) {
  const { t } = useTranslation();
  const state = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      // Recomputed on every transaction, so the AI segment appears and disappears with the
      // selection rather than lagging a keystroke behind it.
      hasSelection: !current.state.selection.empty,
      bold: current.isActive("bold"),
      italic: current.isActive("italic"),
      strike: current.isActive("strike"),
      code: current.isActive("code"),
      h1: current.isActive("heading", { level: 1 }),
      h2: current.isActive("heading", { level: 2 }),
      h3: current.isActive("heading", { level: 3 }),
      bulletList: current.isActive("bulletList"),
      orderedList: current.isActive("orderedList"),
      blockquote: current.isActive("blockquote"),
      codeBlock: current.isActive("codeBlock"),
      canUndo: current.can().undo(),
      canRedo: current.can().redo(),
    }),
  });

  return (
    <div
      role="toolbar"
      aria-label={t("Formatting")}
      className="sticky top-0 z-10 mb-4 flex flex-wrap items-center gap-0.5 rounded-md border border-divider bg-bg px-1.5 py-1"
    >
      <ToolbarButton
        icon={TextHOne}
        label={t("Heading 1")}
        active={state.h1}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      />
      <ToolbarButton
        icon={TextHTwo}
        label={t("Heading 2")}
        active={state.h2}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      />
      <ToolbarButton
        icon={TextHThree}
        label={t("Heading 3")}
        active={state.h3}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      />
      <Separator />
      <ToolbarButton
        icon={TextB}
        label={t("Bold")}
        active={state.bold}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        icon={TextItalic}
        label={t("Italic")}
        active={state.italic}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <ToolbarButton
        icon={TextStrikethrough}
        label={t("Strikethrough")}
        active={state.strike}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      />
      <ToolbarButton
        icon={Code}
        label={t("Inline code")}
        active={state.code}
        onClick={() => editor.chain().focus().toggleCode().run()}
      />
      <Separator />
      <ToolbarButton
        icon={ListBullets}
        label={t("Bullet list")}
        active={state.bulletList}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolbarButton
        icon={ListNumbers}
        label={t("Numbered list")}
        active={state.orderedList}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <ToolbarButton
        icon={Quotes}
        label={t("Quote")}
        active={state.blockquote}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      />
      <ToolbarButton
        icon={CodeBlock}
        label={t("Code block")}
        active={state.codeBlock}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      />
      <Separator />
      <ToolbarButton
        icon={ArrowUUpLeft}
        label={t("Undo")}
        disabled={!state.canUndo}
        onClick={() => editor.chain().focus().undo().run()}
      />
      <ToolbarButton
        icon={ArrowUUpRight}
        label={t("Redo")}
        disabled={!state.canRedo}
        onClick={() => editor.chain().focus().redo().run()}
      />
      {ai && state.hasSelection ? (
        <>
          <Separator />
          <SelectionAiMenu
            actions={ai.actions}
            onSelectAction={ai.onSelectAction}
            onDoAction={ai.onDoAction}
            {...(ai.onCustomAction ? { onCustomAction: ai.onCustomAction } : {})}
            lastLane={ai.lastLane ?? null}
            disabled={ai.busy === true}
          />
        </>
      ) : null}
    </div>
  );
}
